import React, { useState, useEffect } from 'react';
import { Grid, theme } from 'antd';
import { InfoCircleFilled } from '@ant-design/icons';
import { observer } from 'mobx-react';
import { message, notification } from 'antd';
import { useTranslation } from 'react-i18next';

import { permissions } from '@/constants';
import FlowSection from './components/FlowSection/FlowSection';
import EcoSelectStation from '@/comp/EcoSelectStation/EcoSelectStation';
import { useStores } from '@/hooks/use-stores';
import GetConnectivityIconAndStatus from '@/comp/MapBuilder/nodes/compressor/GetConnectivityIconAndStatus/GetConnectivityIconAndStatus';
import { CompressorActivityStateEnum, ControlModeEnum, DeviceConnectivity } from '@/constants/enums';
import { TestState } from '@/store/InstallationValidation/constants';
import logger from '@/utils/logger';
import StationStages from '@/comp/StationStages/StationStages';
import { useScrollMovement } from '@/hooks/useScrollMovement';

import ButtonRenderer from './components/ButtonGroups/ButtonGroups';
import MobileMainDrawer from './components/MobileMainDrawer/MobileMainDrawer';
import { formatTestDate, NotificationType, SKELETON_MOCK_DATA } from './InstallationValidation.utils';
import { StyledInstallationPage } from './InstallationValidationPage.styles';
import AlertValidationInstallation from './components/AlertComponents/AlertValidationInstallation';
import MobileCommonDrawer from './components/MobileCommonDrawer/MobileCommonDrawer';
import { HistoryTestDropDown } from './components/HistoryTests/HistoryTestDropDown';
import { LineBreak } from './components/HistoryTests/HistoryTest.styles';
import { CancelTestModal } from './components/CancelModal/CancelTestModal';

const { useBreakpoint } = Grid;

const InstallationValidationPage = observer(() => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);
  const [isCancelTestOpen, setCancelTestOpen] = useState(false);
  const { installationValidationStore, stationStore } = useStores();
  const screens = useBreakpoint();
  const testState: TestState = installationValidationStore?.testState;
  const isTestTimeVisible = isMobile && testState === TestState.RUNNING;
  const [api, contextHolder] = notification.useNotification();
  const { useToken } = theme;
  const { token } = useToken();
  const { drawerVisible, setDrawerVisible } = useScrollMovement(isMobile, {
    containerSelector: '.installer-main-container',
  });

  const {
    stationTestData,
    isStartingTest,
    isEndingTest,
    isSubmitting,
    isStationLoading,
    isCancelTest,
    historyTests,
    testedBy,
    updateLastTestData,
  } = installationValidationStore || {};

  const flowSection = isStationLoading ? SKELETON_MOCK_DATA : stationTestData?.testDevicesScenario ?? [];

  const testDate = formatTestDate(stationTestData?.endedAt);

  //Hard coding for now, To be changed in next sprints
  const connectivityIconAndStatus = GetConnectivityIconAndStatus({
    connectivity: DeviceConnectivity.Connected,
    activity: CompressorActivityStateEnum.Load,
    extendedActivity: null,
    controlMode: ControlModeEnum.Controllable,
    isSelectedOnMap: null,
    size: '42px',
  });

  //In future we have to check if we can add maximum-scale=1 to our viewport meta tag in index.html
  //That effects the complete app, so for now we are doing it here
  //This is required to solve IOS viewport zoom issue when keyboard is opened
  useEffect(() => {
    const originalViewport = document.querySelector('meta[name="viewport"]');
    const originalContent = originalViewport?.getAttribute('content');

    originalViewport?.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1');

    return () => {
      if (originalViewport && originalContent) {
        originalViewport.setAttribute('content', originalContent);
      }
      installationValidationStore.reset();
    };
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(!screens.md);

      if (screens.md) {
        setDrawerVisible(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, [screens.md, setDrawerVisible]);

  useEffect(() => {
    logger('station validation data refresh with station');
    installationValidationStore.initTest(stationStore?.currentStation?.id);
  }, [stationStore?.currentStation?.id]);

  useEffect(() => {
    if (
      (testState === TestState.NEEDS_REVIEW || testState === TestState.TEST_SUCCESS) &&
      stationTestData &&
      !isMobile
    ) {
      const unresolvedCompressors = installationValidationStore.unresolvedCompressors.length;
      const resolvedCompressors = installationValidationStore.resolvedCompressors.length;
      if (unresolvedCompressors !== 0) {
        openNotification(
          'warning',
          t('UNRESOLVED_COMPRESSORS_TITLE'),
          t('UNRESOLVED_COMPRESSORS_DESC', { count: unresolvedCompressors }),
          5,
        );
      } else if (resolvedCompressors > 0) {
        openNotification('success', t('TEST_SUCCESS_TITLE'), t('TEST_SUCCESS_DESC'), 5);
      }
    }
  }, [testState, stationTestData]);

  const handleAlertTest = () => {
    installationValidationStore.initTest(stationStore?.currentStation?.id);
    installationValidationStore.setShowIsTestAlreadyRunning(false);
    installationValidationStore.setShowIsTestAlreadySubmitted(false);
  };

  const closeAlert = () => {
    installationValidationStore.setShowIsTestAlreadyRunning(false);
    installationValidationStore.setShowIsTestAlreadySubmitted(false);
  };

  const handleStartTest = () => {
    installationValidationStore.startTest();
  };

  const handleCancelTest = () => {
    setCancelTestOpen(true);
  };

  const handleConfirmCancelTest = () => {
    setCancelTestOpen(false);
    installationValidationStore.cancelTest();
  };

  const handleTestAgain = () => {
    installationValidationStore.continueTest();
  };

  const handleEndTest = () => {
    installationValidationStore.endTest();
  };

  const handleSubmitReport = async () => {
    message.config({
      top: isMobile ? window.innerHeight - 150 : 20,
      maxCount: 3,
    });

    try {
      const success = await installationValidationStore.submitReport();
      if (success) {
        message.info({
          content: t('REPORT_SUBMIT_SUCCESS'),
          duration: 3,
          icon: <InfoCircleFilled style={{ color: token.colorInfo }} />,
        });
      }
    } catch (error) {
      message.error({
        content: t('REPORT_SUBMIT_ERROR'),
        duration: 3,
      });
    }
  };

  const handleSubmitClick = () => {
    const unresolvedCompressors = installationValidationStore.unresolvedCompressors.length;
    if (unresolvedCompressors > 0 && !installationValidationStore.readyForSubmit) {
      if (!isMobile) {
        openNotification(
          'warning',
          t('UNRESOLVED_COMPRESSORS_TITLE'),
          t('UNRESOLVED_COMPRESSORS_DESC', { count: unresolvedCompressors }),
          5,
        );
      }
      return false;
    }
    return true;
  };

  const openNotification = (type: NotificationType, message: string, description: string, duration = 5) => {
    api[type]({
      message: message,
      description: description,
      placement: 'bottomLeft',
      duration: duration,
    });
  };

  return (
    <StyledInstallationPage $testTimeVisible={isTestTimeVisible}>
      <div className="test-time-banner">
        <span>{t('TESTING_IN_PROGRESS', { time: installationValidationStore.remainingTimeFormatted })}</span>
      </div>

      {!isMobile ? (
        <CancelTestModal
          open={isCancelTestOpen}
          onCancel={() => setCancelTestOpen(false)}
          onConfirm={handleConfirmCancelTest}
          loading={isCancelTest}
          isMobile={isMobile}
        />
      ) : (
        <MobileCommonDrawer
          type="cancelTest"
          message={t('cancel_button_content')}
          primaryButton={{
            text: t('CANCEL_TEST'),
            onClick: () => {
              handleConfirmCancelTest();
            },

            variant: 'primary',
          }}
          secondaryButton={{
            text: t('continue_test'),
            onClick: () => {
              setCancelTestOpen(false);
            },
            variant: 'secondary',
          }}
          isOpen={isCancelTestOpen}
          onClose={() => setCancelTestOpen(false)}
        />
      )}
      <div className="installer-main-container">
        {contextHolder}
        <div className="installer-station-select-header">
          <div className="installer-station-select">
            <EcoSelectStation requiredPermission={permissions.ACCESS_INSTALLATION_VALIDATION_VIEW} />
          </div>
          <StationStages />
        </div>
        {installationValidationStore.showIsTestAlreadySubmitted && (
          <AlertValidationInstallation
            message={t('INSTALLATION_VALIDATION_SUBMITTED_TITLE')}
            description={t('INSTALLATION_VALIDATION_SUBMITTED_CONTENT')}
            onClose={closeAlert}
            onRefresh={handleAlertTest}
          />
        )}
        {installationValidationStore.showIsTestAlreadyRunning && (
          <AlertValidationInstallation
            message={t('INSTALLATION_VALIDATION_IN_PROGRESS_TITLE')}
            description={t('INSTALLATION_VALIDATION_IN_PROGRESS_CONTENT')}
            onClose={closeAlert}
            onRefresh={handleAlertTest}
          />
        )}
        <div className="installer-header">
          <h1 className="installer-header-text">{t('INSTALLATION_VALIDATION_TITLE')}</h1>

          {isMobile && testState === TestState.SUBMITTED && (
            <HistoryTestDropDown
              historyTests={historyTests}
              testedBy={testedBy}
              handleUpdateLastTestData={updateLastTestData}
              testDate={testDate}
              isMobile={true}
            />
          )}

          {!isMobile && (
            <ButtonRenderer
              testState={testState}
              onStartTest={handleStartTest}
              onCancelTest={handleCancelTest}
              onTestAgain={handleTestAgain}
              onEndTest={handleEndTest}
              onMobileSubmitReport={handleSubmitReport}
              handleSubmitClick={handleSubmitClick}
              isStartingTest={isStartingTest}
              isEndingTest={isEndingTest}
              isCancelTest={isCancelTest}
              testDate={testDate}
              handleUpdateLastTestData={updateLastTestData}
              historyTests={historyTests}
              isSubmitting={isSubmitting}
              testedBy={testedBy}
              remainingTimeFormatted={installationValidationStore.remainingTimeFormatted}
              numberOfUnresolvedCompressors={installationValidationStore.unresolvedCompressors.length}
              numberOfResolvedCompressors={installationValidationStore.resolvedCompressors.length}
              testId={stationTestData?.id}
              isLoading={isStationLoading}
            />
          )}
        </div>
        {isMobile && <LineBreak $height={20} />}

        {flowSection.map(section => (
          <FlowSection
            key={section.groupFlowSensorId}
            sectionTitle={section.flowSensorName}
            compressors={section.compressors}
            connectivityIconAndStatus={connectivityIconAndStatus}
            flowSumStatus={section.FLOW}
            testState={testState}
            isLoading={isStationLoading}
          />
        ))}

        {isMobile && (
          <MobileMainDrawer
            visible={drawerVisible}
            testState={testState}
            onStartTest={handleStartTest}
            onCancelTest={handleCancelTest}
            onTestAgain={handleTestAgain}
            onEndTest={handleEndTest}
            onSubmitReport={handleSubmitReport}
            handleSubmitClick={handleSubmitClick}
            isStartingTest={isStartingTest}
            isEndingTest={isEndingTest}
            isCancelTest={isCancelTest}
            handleUpdateLastTestData={updateLastTestData}
            isSubmitting={isSubmitting}
            historyTests={historyTests}
            testedBy={testedBy}
            testDate={testDate}
            remainingTimeFormatted={installationValidationStore.remainingTimeFormatted}
            numberOfUnresolvedCompressors={installationValidationStore.unresolvedCompressors.length}
            numberOfResolvedCompressors={installationValidationStore.resolvedCompressors.length}
            testId={stationTestData?.id}
          />
        )}
      </div>
    </StyledInstallationPage>
  );
});

export default InstallationValidationPage;
